import React from 'react'
import { Box, Typography } from '@mui/material'

import { isAnalyzerLoading } from 'Pages/AnalyzersV2/@signals'
import { buildVerdict } from 'Pages/AnalyzersV2/@components/PositionVerdict/@utils'
import { dsColorNew } from '@palette'
import { AskIrisButton } from '@components'
import { buildPositionKpis } from 'Pages/AnalyzersV2/@components/PositionKpis/@utils'
import { readingEvidence } from 'Pages/AnalyzersV2/@services/askEvidence'
import { analyzerAskContext } from 'Pages/AnalyzersV2/@services/askScope'
import { responseData } from 'Pages/AnalyzersV2/@signals'
import { findCurrency } from 'Pages/AnalyzersV2/Position/@utils'


const textSx = { fontSize: 14, color: dsColorNew.textDark }
const boldSx = { ...textSx, fontWeight: 700, color: dsColorNew.textTitle }

export default function PositionVerdict({ mainData, mainName }) {
	if (isAnalyzerLoading.value) return null

	const verdict = buildVerdict(mainData, mainName)

	if (!verdict) return null

	const { deltaText, rankText, cheapestText, isOftenCheapest, underText, stanceText } = verdict

	// "Ask Iris" at the end of the line (CU-869fkdhjv phase 3, UX § b 4): the sentence as shown and the five KPIs
	const getAsk = () => {
		// the KPI cards' currency, read as `Position/Content` reads it (that file is CU-869fmpqfm's, plan § 4)
		const currency = findCurrency(responseData.value?.trows || [])
		const ctx = analyzerAskContext({ currency, mainName })
		const columns = (responseData.value?.xlist || []).filter(Boolean)
		return readingEvidence({
			verdict, kpis: buildPositionKpis(mainData, currency),
			labels: [...ctx.labels, columns.length ? { range: [columns[0], columns[columns.length - 1]] } : null],
			params: ctx.params, freshness: ctx.freshness,
		})
	}

	// One sentence on a white card with the app blue edge, as the app's cards
	return <Box sx={{
		px: 2.5, py: 1.75, borderRadius: '12px', backgroundColor: dsColorNew.white,
		border: `1px solid ${dsColorNew.grayBorder}`, borderLeft: `4px solid ${dsColorNew.appBlue}`,
	}}>
		<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: .5 }}>
			<Typography sx={boldSx}>Read:</Typography>

			<Typography component="span" sx={textSx}>
				{mainName} sits
			</Typography>
			{!!deltaText && <Typography sx={boldSx}>{deltaText}</Typography>}
			<Typography component="span" sx={textSx}>
				vs the blended market average
			</Typography>

			{!!rankText && <>
				<Typography component="span" sx={textSx}>and ranks</Typography>
				<Typography sx={boldSx}>{rankText}</Typography>
			</>}

			{!!cheapestText && <Box component="span" sx={{
				display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '10px',
				fontSize: 12, fontWeight: 600, lineHeight: 1.5, whiteSpace: 'nowrap',
				color: isOftenCheapest ? dsColorNew.successText : dsColorNew.dangerRed,
				backgroundColor: isOftenCheapest ? dsColorNew.successBg : dsColorNew.dangerBg,
			}}>
				{cheapestText}
			</Box>}

			<Typography component="span" sx={textSx}>{underText}</Typography>
			{!!stanceText && <Typography component="span" sx={textSx}>{stanceText}</Typography>}
			<Box sx={{ ml: 'auto', pl: 1, display: 'flex', '&:empty': { display: 'none' } }}>
				<AskIrisButton variant="label" tooltip="Ask Iris about this reading" getAsk={getAsk}/>
			</Box>
		</Box>
	</Box>
}
